import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/forms/Combobox";
import { useReferences } from "@/hooks/useReferences";

// Reusable address fieldset used everywhere addresses appear.
export const AddressFields = ({ value = {}, onChange, testIdPrefix = "address" }) => {
  const { references } = useReferences();
  const states = references.us_states || [];
  const countries = references.countries || [];
  const v = { line1: "", line2: "", city: "", state: "", zip: "", country: "", ...value };
  const set = (k, val) => onChange({ ...v, [k]: val });
  const isUS = v.country === "US";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-1.5 sm:col-span-2">
        <Label>Address line 1</Label>
        <Input value={v.line1} onChange={(e) => set("line1", e.target.value)} data-testid={`${testIdPrefix}-line1`} placeholder="Street address" />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label>Address line 2</Label>
        <Input value={v.line2} onChange={(e) => set("line2", e.target.value)} data-testid={`${testIdPrefix}-line2`} placeholder="Apt, suite, etc. (optional)" />
      </div>
      <div className="space-y-1.5">
        <Label>City</Label>
        <Input value={v.city} onChange={(e) => set("city", e.target.value)} data-testid={`${testIdPrefix}-city`} />
      </div>
      <div className="space-y-1.5">
        <Label>State / Region</Label>
        {isUS ? (
          <Select value={v.state || undefined} onValueChange={(val) => set("state", val)}>
            <SelectTrigger data-testid={`${testIdPrefix}-state`}><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {states.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input value={v.state} onChange={(e) => set("state", e.target.value)} data-testid={`${testIdPrefix}-state`} />
        )}
      </div>
      <div className="space-y-1.5">
        <Label>ZIP / Postal code</Label>
        <Input value={v.zip} onChange={(e) => set("zip", e.target.value)} data-testid={`${testIdPrefix}-zip`} />
      </div>
      <div className="space-y-1.5">
        <Label>Country</Label>
        <Combobox options={countries} value={v.country || ""} onChange={(val) => set("country", val)} placeholder="Select country" searchPlaceholder="Search countries…" emptyText="No country found." testId={`${testIdPrefix}-country`} />
      </div>
    </div>
  );
};

// Lightweight location fieldset (city/state/zip/country only) used by profiles.
export const LocationFields = ({ value = {}, onChange, testIdPrefix = "location" }) => {
  const { references } = useReferences();
  const states = references.us_states || [];
  const countries = references.countries || [];
  const v = { city: "", state: "", zip: "", country: "", ...value };
  const set = (k, val) => onChange({ ...v, [k]: val });
  const isUS = v.country === "US";
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label>City</Label>
        <Input value={v.city} onChange={(e) => set("city", e.target.value)} data-testid={`${testIdPrefix}-city`} />
      </div>
      <div className="space-y-1.5">
        <Label>State / Region</Label>
        {isUS ? (
          <Select value={v.state || undefined} onValueChange={(val) => set("state", val)}>
            <SelectTrigger data-testid={`${testIdPrefix}-state`}><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent className="max-h-64">{states.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input value={v.state} onChange={(e) => set("state", e.target.value)} data-testid={`${testIdPrefix}-state`} />
        )}
      </div>
      <div className="space-y-1.5">
        <Label>ZIP / Postal code</Label>
        <Input value={v.zip} onChange={(e) => set("zip", e.target.value)} data-testid={`${testIdPrefix}-zip`} />
      </div>
      <div className="space-y-1.5">
        <Label>Country</Label>
        <Combobox options={countries} value={v.country || ""} onChange={(val) => set("country", val)} placeholder="Select country" searchPlaceholder="Search countries…" emptyText="No country found." testId={`${testIdPrefix}-country`} />
      </div>
    </div>
  );
};
